/**
 * NeighborhoodContext - Shared state management for HALO NeighborhoodModule
 *
 * Provides commute state persistence between NeighborhoodSpoke (fullscreen)
 * and NeighborhoodModule (collapsed) views using React Context API.
 */

import type React from 'react';
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import type { NeighborhoodState, NeighborhoodActions, NeighborhoodContextValue } from '../types';
import neighborhoodStateManager from './NeighborhoodStateManager';

/**
 * React Context for neighborhood state
 */
const NeighborhoodContext = createContext<NeighborhoodContextValue | null>(null);

/**
 * Hook to consume NeighborhoodContext
 *
 * @throws Error if used outside of NeighborhoodProvider
 */
export const useNeighborhoodContext = (): NeighborhoodContextValue => {
  const context = useContext(NeighborhoodContext);

  if (!context) {
    throw new Error(
      'useNeighborhoodContext must be used within a NeighborhoodProvider. ' +
        'Wrap NeighborhoodTrigger with <NeighborhoodProvider> to provide state context.',
    );
  }

  return context;
};

/**
 * Safe hook to consume NeighborhoodContext that returns null if no provider exists
 *
 * Use this when component might be rendered outside of NeighborhoodProvider
 */
export const useNeighborhoodContextSafe = (): NeighborhoodContextValue | null => {
  const context = useContext(NeighborhoodContext);
  return context;
};

/**
 * Props for NeighborhoodProvider component
 */
type NeighborhoodProviderProps = {
  children: React.ReactNode;
};

/**
 * Provider component for NeighborhoodContext
 *
 * Connects to singleton state manager to enable state sharing between
 * separate provider instances in the module system architecture.
 */
export const NeighborhoodProvider = ({ children }: NeighborhoodProviderProps) => {
  // Connect to singleton state manager
  const [state, setState] = useState<NeighborhoodState>(neighborhoodStateManager.getState());

  // Subscribe to singleton state changes
  useEffect(() => {
    const unsubscribe = neighborhoodStateManager.subscribe((newState) => {
      setState(newState);
    });

    return unsubscribe;
  }, []);

  /**
   * Get actions from singleton manager - these are stable references
   * that update the shared state across all provider instances
   */
  const actions = useMemo<NeighborhoodActions>(() => neighborhoodStateManager.getActions(), []);

  /**
   * Memoized context value to prevent unnecessary re-renders
   */
  const contextValue = useMemo<NeighborhoodContextValue>(
    () => ({
      state,
      actions,
    }),
    [state, actions],
  );

  return <NeighborhoodContext.Provider value={contextValue}>{children}</NeighborhoodContext.Provider>;
};
